import { Directive, ElementRef, OnDestroy, WritableSignal, effect, input, signal } from '@angular/core';
import { PlatformService } from 'lib/platform/index.js';
import tippy, { Instance, Placement, Tippy } from 'tippy.js';
import { ScreenService } from '../../screen/index.js';

export type TooltipTheme = 'default';
export type TooltipLocation = Placement;

@Directive({
	selector: '[libTooltip]',
	standalone: false
})
export class TooltipDirective implements OnDestroy {
	public readonly text: WritableSignal<string | undefined> = signal(undefined);

	public readonly libTooltip = input.required<string | undefined>();
	public readonly theme = input<TooltipTheme>('default');
	public readonly placement = input<TooltipLocation | undefined>('bottom');

	private readonly canUseTippy: boolean;
	private initialized: boolean = false;
	private tippyInstances?: Instance<any>[] | Instance<any>;

	constructor(private hostElement: ElementRef, screenService: ScreenService, platformService: PlatformService) {
		const screen = screenService.currentScreen();

		let canUseTippy: boolean = false;

		if (platformService.isBrowser) {
			if (screen.isMobileView || screen.isTabletView) {
				/* empty */
			} else {
				canUseTippy = true;
			}
		}

		this.canUseTippy = platformService.isBrowser;

		effect(() => {
			const tooltipText = this.libTooltip();
			this.text.set(tooltipText);

			if (!this.canUseTippy) {
				return;
			}

			if (!this.initialized && tooltipText) {
				this.initialized = true;

				this.tippyInstances = (tippy as any as Tippy)(this.hostElement.nativeElement, {
					content: tooltipText,
					duration: [75, 75],
					animation: 'scale',
					hideOnClick: true,
					theme: this.theme() === 'default' ? 'coachway' : 'coachway',
					placement: this.placement(),
					arrow: true
				});
			}

			if (this.tippyInstances) {
				if (tooltipText) {
					// tooltip was set, enable it
					if (Array.isArray(this.tippyInstances)) {
						for (const instance of this.tippyInstances) {
							instance.enable();
						}
					} else {
						this.tippyInstances.enable();
					}
				} else {
					// tooltip was unset, disable it
					if (Array.isArray(this.tippyInstances)) {
						for (const instance of this.tippyInstances) {
							instance.disable();
						}
					} else {
						this.tippyInstances.disable();
					}
				}
			}
		});
	}

	ngOnDestroy(): void {
		if (!this.canUseTippy) {
			return;
		}
		this.destroyTippyInstances();
	}

	private destroyTippyInstances(): void {
		if (this.tippyInstances) {
			if (Array.isArray(this.tippyInstances)) {
				for (const instance of this.tippyInstances) {
					instance.destroy();
				}
			} else {
				this.tippyInstances.destroy();
			}

			this.tippyInstances = undefined;
		}
	}
}
